import type {CourseExpandedCommon} from '../../../api/course';
import type {ProgramApiModel} from '../../../api/program';

import {envManager} from '../../env';
import {isEditionSigned} from '../edition/isEditionSigned';
import {getFileSrc} from '../file';
import {getLastEdition} from '../program/getLastEdition';
import {isMeMainCoachForProgram} from '../program/isMeMainCoachForProgram';
import {canCourseBeStarted} from './canCourseBeStarted';
import {getCourseDurationString} from './getCourseDurationString';
import {getCourseLabel} from './getCourseLabel';
import {getCourseStatusString} from './getCourseStatusString';
import {isCourseCorporate} from './isCourseCorporate';
import {isCourseDeletable} from './isCourseDeletable';
import {isCourseEnded} from './isCourseEnded';
import {isCourseIndividual} from './isCourseIndividual';

export class CourseModel {
  constructor(readonly course: CourseExpandedCommon) {}

  get avatarUrl() {
    return getFileSrc(this.course.program?.avatar, 250).url ?? '';
  }

  get canBeStarted() {
    return canCourseBeStarted(this.course);
  }

  get canBeStopped() {
    if (envManager.isB2b || isCourseCorporate(this.course)) {
      return false;
    }

    return (
      this.isMeMainCoach &&
      this.course.status === 'ongoing' &&
      isCourseEnded(this.course)
    );
  }

  get clientAvatarUrl() {
    return getFileSrc(this.course.client.avatar, 250).url ?? '';
  }

  get clientName() {
    return this.course.client.name;
  }

  get clientsCount() {
    return this.course.counters.memberships.accepted;
  }

  get durationString() {
    return getCourseDurationString(this.course);
  }

  get groupSize() {
    return this.course.edition?.group_size;
  }

  get hasCustomLabel() {
    return Boolean(this.course.label);
  }

  get id() {
    return this.course._id;
  }

  get isArchived() {
    return this.course.status === 'archived';
  }

  get isAutoAccept() {
    if (this.course.status !== 'planned' || !this.groupSize) {
      return false;
    }

    const {memberships} = this.course.counters;
    const occupiedSeats = memberships.accepted + memberships.proposed;

    return occupiedSeats < this.groupSize;
  }

  get isCourseUpdatable() {
    return (
      this.course.status === 'planned' &&
      this.isLastEditionSigned &&
      this.lastEdition &&
      this.course.edition_id !== this.lastEdition._id
    );
  }

  get isDeletable() {
    return isCourseDeletable(this.course);
  }

  get isIndividual() {
    return isCourseIndividual(this.course);
  }

  get isLastEditionSigned() {
    return this.lastEdition ? isEditionSigned(this.lastEdition) : false;
  }

  get isMeMainCoach() {
    if (!this.course.program.user_id) {
      return false;
    }

    return isMeMainCoachForProgram(this.course.program as ProgramApiModel);
  }

  get label() {
    return getCourseLabel(this.course);
  }

  get lastEdition() {
    return getLastEdition(this.course.program);
  }

  get programTitle() {
    return this.course.program?.title;
  }

  get statusString() {
    return getCourseStatusString(this.course);
  }
}
